import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useEffect, useCallback } from 'react';
import * as React from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useWorkersService } from '@restworld/data-services';
import { Heading, Spinner } from '@restworld/ui-ds-base';
import { WorkerJPSavedResearch } from '@restworld/utility-types';
import { useVariableOptions } from '@restworld/utils-common';

import SavedResearch from './saved-research';
import SavedResearchNotificationChangeModal from './save-research-notification-change-modal';
import { notificationResearchIdAtom } from './save-research-tab.atoms';

const SaveResearchTab = () => {
  const { fetchVariable, variableOptions } = useVariableOptions();
  const workerServices = useWorkersService();
  const queryClient = useQueryClient();

  const listSaveResearch = React.useCallback(
    async () =>
      (
        await workerServices.listSavedResearches({
          limit: 999,
          offset: 0,
        })
      ).data,
    [workerServices]
  );

  useEffect(() => {
    fetchVariable(['domain_interest']);
  }, [fetchVariable]);

  const { data, isFetching } = useQuery({
    queryKey: ['listSaveResearch'],
    queryFn: listSaveResearch,
    refetchOnWindowFocus: false,
  });

  const handleTitleUpdate = useCallback(
    async (id: number, title: string) => {
      try {
        await workerServices.updateSavedResearch({ id, title });
        queryClient.invalidateQueries({ queryKey: ['listSaveResearch'] });
      } catch (error) {
        console.warn('Failed to update saved research title', error);
      }
    },
    [workerServices, queryClient]
  );

  const savedResearches = useMemo(
    () =>
      data
        ?.sort((a: WorkerJPSavedResearch, b: WorkerJPSavedResearch) => b.id - a.id)
        .map((item) => {
          return (
            <li key={item.id}>
              <SavedResearch
                data={{ ...item }}
                domainInterest={variableOptions['domain_interest']}
                onTitleUpdate={handleTitleUpdate}
              />
            </li>
          );
        }),
    [data, variableOptions, handleTitleUpdate]
  );

  const saveResearchId = useAtomValue(notificationResearchIdAtom);
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-10 lg:flex-row lg:justify-between">
      <div className="flex flex-col gap-2">
        <Heading className="leading-7.5">{t('user-settings:saved_searches')}</Heading>
        <Heading size={6} weight="normal" color="muted-foreground" className="leading-5.25">
          {t('user-settings:opt_for_notifications_or_deleting_saved_searches')}
        </Heading>
      </div>
      <div className="flex flex-col gap-8 lg:w-1/2">
        <div className="self-end lg:pr-8">
          <Heading size={6} weight="medium" className="leading-5.25 text-gray-400">
            {t('common:notifications')}
          </Heading>
        </div>
        <ul className="flex flex-col gap-8 lg:max-h-104 lg:overflow-y-auto lg:pr-2">
          {isFetching && !data?.length && (
            <li key="loading">
              <Spinner color="primary" size="lg" />
            </li>
          )}
          {savedResearches}
          <SavedResearchNotificationChangeModal key={saveResearchId} />
        </ul>
      </div>
    </div>
  );
};

export default SaveResearchTab;
